import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  getPublishedPosts, 
  getFeaturedPosts, 
  getAllCategories, 
  getAllTags,
  BlogPost,
  BlogCategory,
  BlogTag
} from '../services/blogService';
import { Helmet } from 'react-helmet';
import '../styles/BlogPage.scss';
import BlogFooter from '../components/BlogFooter';

const BlogPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const categoriesSectionRef = useRef<HTMLDivElement>(null);
  const postsPerPage = 6;

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Get featured posts
        const featured = await getFeaturedPosts(3);
        setFeaturedPosts(featured);
        
        // Get categories and tags
        const allCategories = await getAllCategories();
        setCategories(allCategories);
        
        const allTags = await getAllTags();
        setTags(allTags.filter(tag => tag.post_count > 0));
        
        // Get posts for first page
        const { data, count } = await getPublishedPosts(1, postsPerPage);
        setPosts(data);
        setTotalPages(Math.ceil((count || 0) / postsPerPage));
      } catch (error) {
        console.error('Error fetching blog data:', error);
      }
      
      setLoading(false);
    };
    
    fetchData();
  }, []);

  // Fetch posts when page, category, tag, or search changes
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      
      try {
        const { data, count } = await getPublishedPosts(
          currentPage, 
          postsPerPage, 
          activeCategory || undefined,
          activeTag || undefined,
          searchQuery
        );
        
        setPosts(data);
        setTotalPages(Math.ceil((count || 0) / postsPerPage));
      } catch (error) {
        console.error('Error fetching posts:', error);
      }
      
      setLoading(false);
    };
    
    fetchPosts();
  }, [currentPage, activeCategory, activeTag, searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleCategoryClick = (categorySlug: string) => {
    if (activeCategory === categorySlug) {
      setActiveCategory(null);
    } else {
      setActiveCategory(categorySlug);
      setActiveTag(null); // Reset tag when category is selected
    }
    setCurrentPage(1);
  };

  const handleTagClick = (tagSlug: string) => {
    if (activeTag === tagSlug) {
      setActiveTag(null);
    } else {
      setActiveTag(tagSlug);
      setActiveCategory(null); // Reset category when tag is selected
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setActiveCategory(null);
    setActiveTag(null);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleBrowseCategories = () => {
    setShowMobileCategories(true);
    if (categoriesSectionRef.current) {
      categoriesSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Extract first image from post content for thumbnail
  const extractFirstImage = (content: string) => {
    const imgMatch = content.match(/<img[^>]+src="([^">]+)"/i);
    return imgMatch ? imgMatch[1] : null;
  };

  // Truncate text to specified length
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  // Remove HTML tags from text
  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="blog-page">
      <Helmet>
        <title>Blog | PLAB2 Simulator</title>
        <meta name="description" content="Latest articles, guides, and news about PLAB2 examination preparation and medical practice scenarios." />
        <meta property="og:title" content="Blog | PLAB2 Simulator" />
        <meta property="og:description" content="Latest articles, guides, and news about PLAB2 examination preparation and medical practice scenarios." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog | PLAB2 Simulator" />
        <meta name="twitter:description" content="Latest articles, guides, and news about PLAB2 examination preparation and medical practice scenarios." />
      </Helmet>

      <div className="blog-content-container">
        <div className="blog-header">
          <div className="blog-header-content">
            <h1>PLAB2 Simulator Blog</h1>
            <p>Insights, tips, and news to help you succeed in your PLAB2 examination</p>
            <div className="cta-buttons">
              <Link to="/" className="cta-button primary">Try Our Simulator</Link>
              <button onClick={handleBrowseCategories} className="cta-button secondary">Browse Categories</button>
            </div>
          </div>
        </div>

        {featuredPosts.length > 0 && (
          <section className="featured-posts">
            <h2>Featured Articles</h2>
            <div className="featured-posts-grid">
              {featuredPosts.map(post => (
                <div className="featured-post-card" key={post.id}>
                  <div className="featured-post-image">
                    {(post.featured_image || extractFirstImage(post.content)) ? (
                      <img 
                        src={post.featured_image || extractFirstImage(post.content) || ''} 
                        alt={post.title} 
                      />
                    ) : (
                      <div className="placeholder-image">
                        <span>PLAB2 Simulator</span>
                      </div>
                    )}
                  </div>
                  <div className="featured-post-content">
                    {post.category && (
                      <span className="post-category">{post.category.name}</span>
                    )}
                    <h3>
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </h3>
                    <p>{truncateText(stripHtml(post.summary), 120)}</p>
                    <div className="post-meta">
                      <span className="post-date">
                        {post.published_at && formatDate(post.published_at)}
                      </span>
                      {post.author && (
                        <span className="post-author">
                          By {post.author.first_name} {post.author.last_name}
                        </span>
                      )}
                    </div>
                    <Link to={`/blog/${post.slug}`} className="read-more">
                      Read Article
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="blog-content">
          <main className="blog-main">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading articles...</p>
              </div>
            ) : (
              <>
                <div className="blog-filters-mobile">
                  <button className="filter-button">
                    <i className="fas fa-filter"></i> Filter Posts
                  </button>
                  {(activeCategory || activeTag || searchQuery) && (
                    <button onClick={clearFilters} className="clear-filters">
                      Clear Filters
                    </button>
                  )}
                </div>

                {(activeCategory || activeTag || searchQuery) && (
                  <div className="active-filters">
                    <h3>Active Filters:</h3>
                    {activeCategory && (
                      <span className="filter-tag">
                        Category: {categories.find(c => c.slug === activeCategory)?.name}
                        <button onClick={() => setActiveCategory(null)}>×</button>
                      </span>
                    )}
                    {activeTag && (
                      <span className="filter-tag">
                        Tag: {tags.find(t => t.slug === activeTag)?.name}
                        <button onClick={() => setActiveTag(null)}>×</button>
                      </span>
                    )}
                    {searchQuery && (
                      <span className="filter-tag">
                        Search: {searchQuery}
                        <button onClick={() => setSearchQuery('')}>×</button>
                      </span>
                    )}
                    <button onClick={clearFilters} className="clear-all">
                      Clear All
                    </button>
                  </div>
                )}

                {posts.length === 0 ? (
                  <div className="no-posts">
                    <h2>No posts found</h2>
                    <p>
                      {activeCategory || activeTag || searchQuery
                        ? 'Try changing your filters or search term'
                        : 'Check back soon for new content'}
                    </p>
                    {(activeCategory || activeTag || searchQuery) && (
                      <button onClick={clearFilters} className="clear-filters-button">
                        Clear Filters
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="posts-grid">
                    {posts.map(post => (
                      <article className="post-card" key={post.id}>
                        <div className="post-image">
                          {(post.featured_image || extractFirstImage(post.content)) ? (
                            <Link to={`/blog/${post.slug}`}>
                              <img 
                                src={post.featured_image || extractFirstImage(post.content) || ''} 
                                alt={post.title} 
                              />
                            </Link>
                          ) : (
                            <div className="placeholder-image">
                              <span>PLAB2 Simulator</span>
                            </div>
                          )}
                        </div>
                        <div className="post-content">
                          {post.category && (
                            <Link 
                              to={`/blog/category/${post.category.slug}`} 
                              className="post-category"
                              onClick={(e) => {
                                e.preventDefault();
                                handleCategoryClick(post.category!.slug);
                              }}
                            >
                              {post.category.name}
                            </Link>
                          )}
                          <h2 className="post-title">
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </h2>
                          <p className="post-excerpt">
                            {truncateText(stripHtml(post.summary), 120)}
                          </p>
                          <div className="post-meta">
                            <span className="post-date">
                              {post.published_at && formatDate(post.published_at)}
                            </span>
                            <span className="post-comments">
                              {post.comments_count || 0} comments
                            </span>
                          </div>
                          <Link to={`/blog/${post.slug}`} className="read-more">
                            Read More
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <div className="page-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`page-number ${currentPage === page ? 'active' : ''}`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </main>

          <aside className="blog-sidebar">
            <div className="sidebar-section search-section">
              <h3>Search</h3>
              <form onSubmit={handleSearch} className="search-form">
                <input 
                  type="text" 
                  placeholder="Search articles..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-button">
                  <i className="fas fa-search"></i>
                </button>
              </form>
            </div>

            <div className="sidebar-section" ref={categoriesSectionRef}>
              <h3>Categories</h3>
              <ul className={`category-list ${showMobileCategories ? 'show-mobile' : ''}`}>
                {categories.map(category => (
                  <li 
                    key={category.id}
                    className={activeCategory === category.slug ? 'active' : ''}
                  >
                    <button onClick={() => handleCategoryClick(category.slug)}>
                      {category.name}
                      <span className="post-count">{category.post_count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="sidebar-section">
              <h3>Popular Tags</h3>
              <div className="tag-cloud">
                {tags.map(tag => (
                  <button 
                    key={tag.id}
                    onClick={() => handleTagClick(tag.slug)}
                    className={`tag ${activeTag === tag.slug ? 'active' : ''}`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="sidebar-section cta-section">
              <h3>Try Our PLAB2 Simulator</h3>
              <p>Prepare for your exam with realistic patient scenarios and get instant feedback.</p>
              <div className="cta-features">
                <div className="cta-feature">
                  <i className="fas fa-user-md"></i>
                  <span>Realistic Simulations</span>
                </div>
                <div className="cta-feature">
                  <i className="fas fa-comments"></i>
                  <span>Interactive Conversations</span>
                </div>
                <div className="cta-feature">
                  <i className="fas fa-chart-line"></i>
                  <span>Performance Tracking</span>
                </div>
              </div>
              <Link to="/" className="cta-button">Start Practicing Now</Link>
            </div>
          </aside>
        </div>
      </div>
      
      <BlogFooter />
    </div>
  );
};

export default BlogPage; 